/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./CVEList.css";

import React from "react";
import { DateTime } from "lib/DateTime";

import { bindActionCreators, Dispatch } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import { CVEsState as RCVEListState, slice } from "store/company/cves.slice";

import { CVEListProps, CVEListState } from "./CVEList.types";

import Widget from "components/ThreeKey/Widget";
import ThreeKeyDataGrid, { DataGridHeaderColumn, DataGridProps } from "components/ThreeKey/DataGrid";
import Pagination from "components/ThreeKey/Pagination";


class CVEList extends React.Component<CVEListProps, CVEListState> {

   constructor(props: CVEListProps) {

      super(props);

      this.state = {
         cveList: []
      }

   }

   public componentDidUpdate(prevProps: CVEListProps) {

      if (prevProps.cves.cveList !== this.props.cves.cveList) this.mapCveDataToCveList();

   }

   public render(): React.ReactNode {

      const columns: DataGridHeaderColumn[] = [
         { caption: "CVE ID", filterable: false, sortable: false, resizable: true },
         { caption: "Score", filterable: false, sortable: false, resizable: true },
         { caption: "Severity", filterable: false, sortable: false, resizable: true },
         { caption: "Published", filterable: false, sortable: false, resizable: true },
         { caption: "Modified", filterable: false, sortable: false, resizable: true },
         { caption: "Description", filterable: false, sortable: false, resizable: true }
      ]

      const cveList: DataGridProps = {

         header: {
            visible: true,
            columns
         },

         data: this.state.cveList,

      }

      const dateControls = (<>

            <div>CVE List</div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "normal", marginLeft: "auto" }}>

               <div>
                  <label htmlFor="" style={{ fontSize: "0.8em"}}>Date range:</label>&nbsp;
                  <select onChange={this.onDateRangeChange} value={this.props.cves.dateRange} disabled={this.props.cves.cvesLoading}>
                     <option value={0}>Custom</option>
                     <option value={1}>Today</option>
                     <option value={2}>Yesterday</option>
                     <option value={3}>Last 2 days</option>
                     <option value={4}>Last 7 days</option>
                     <option value={5}>Last 30 days</option>
                     <option value={6}>Last 90 days</option>
                     <option value={7}>Last 120 days</option>
                     <option value={8}>Last 180 days</option>
                     <option value={9}>Last year</option>
                     <option value={10}>Last 2 years</option>
                     <option value={11}>Last 5 years</option>
                     <option value={12}>Last 10 years</option>
                     <option value={13}>Last 20 years</option>
                     <option value={14}>Last 30 years</option>
                  </select>
               </div>

               &nbsp;&nbsp;&nbsp;

               <div>
                  <label htmlFor="" style={{ fontSize: "0.8em"}}>Date start:</label>&nbsp;
                  <input type="checkbox" name="dateStartCheckbox" checked={this.props.cves.dateStartEnabled} onChange={this.onDateStartEndCheckboxChange} disabled={this.props.cves.cvesLoading} />
                  &nbsp;
                  <input type="date" value={this.props.cves.dateStart} onChange={this.onDateStartChange} disabled={this.props.cves.cvesLoading}/>
               </div>

               &nbsp;&nbsp;&nbsp;

               <div>
                  <label htmlFor="" style={{ fontSize: "0.8em"}}>Date end:</label>&nbsp;
                  <input type="checkbox" name="dateEndCheckbox" checked={this.props.cves.dateEndEnabled} onChange={this.onDateStartEndCheckboxChange} disabled={this.props.cves.cvesLoading} />
                  &nbsp;
                  <input type="date" value={this.props.cves.dateEnd} onChange={this.onDateEndChange} disabled={this.props.cves.cvesLoading} />
               </div>

            </div>

      </>)

      const tkgd = this.props.cves.cveList.totalCount === 0 ? <></> :
         <ThreeKeyDataGrid
            componentName="cve-list"
            {...cveList}
            onItemSelectionChanged={this.onCVESelectionChange}
         />


      const loadedItems = this.props.cves.cveList.totalCount > 10000 ? 10000 : this.props.cves.cveList.totalCount;

      const loadedItemsInfo = this.props.cves.cveList.totalCount > 10000 ?
         <span style={{color: "red"}} dangerouslySetInnerHTML={{ __html: `<span title="Maximum of 10.000 items can be browsed. &#13;Refine your search to limit results.">10000 items out of  ${this.props.cves.cveList.totalCount} ready</span>` }} />
      :
         `${this.props.cves.cveList.totalCount} items ready`;


      return (

         <Widget componentName="cve-list" caption={dateControls} isLoading={this.props.isLoading}>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "3em" }}>

               <Pagination /*hasHomeEnd={true} hasFastMove={true}*/ hideWhenNotNecessary={true} hasPrevNext={true} itemsPerPage={100} itemCount={loadedItems} page={(this.props.cves.cveList.from / 100) + 1} onChange={(page) => this.onPageChange(page)} />

               <div style={{position: "absolute", right: 0, fontSize: "0.8em"}}>{loadedItemsInfo}</div>

            </div>

            {tkgd}

         </Widget>

      )

   }

   private mapCveDataToCveList() {

      const cveList = this.props.cves.cveList.data.map(

         cve => {

            const lmd: number = new Date(cve.lastModifiedDate).getTime();
            const cd: number = new Date(cve.publishedDate).getTime();

            return {
               key: cve.cve.CVE_data_meta.ID,
               data: [
                  cve.cve.CVE_data_meta.ID,
                  cve.impact.baseMetricV3?.cvssV3.baseScore || "",
                  cve.impact.baseMetricV3?.cvssV3.baseSeverity || "",
                  DateTime.toYYYYMMDDHHMM(cd),
                  DateTime.toYYYYMMDDHHMM(lmd),
                  cve.cve.description.description_data[0]?.value || "",
               ]
            }

         }

      );

      this.setState({ cveList });

   }


   private onDateStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {

      this.props.actions.setDateRange({
         range: 0,
         dateStart: e.target.value
      });

   }

   private onDateEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {

      this.props.actions.setDateRange({
         range: 0,
         dateEnd: e.target.value
      });

   }

   private onDateStartEndCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {

      if (e.target.name === "dateStartCheckbox") {

         this.props.actions.setDateRange({
            range: 0,
            dateStartEnabled: e.target.checked
         });

      }

      if (e.target.name === "dateEndCheckbox") {

         this.props.actions.setDateRange({
            range: 0,
            dateEndEnabled: e.target.checked
         });

      }

   }

   private onDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {

      let dateStartEnabled = true;
      let dateEndEnabled = true;

      let newStart = new Date().setHours(0, 0, 0);
      let newEnd = Date.now();

      switch (parseInt(e.target.value)) {
         case 0: break;
         case 1: break;
         case 2: newStart -= (24 * 60 * 60 * 1000) * 1; newEnd = new Date().setHours(0, 0, 0);  break;
         case 3: newStart -= (24 * 60 * 60 * 1000) * 2; break;
         case 4: newStart -= (24 * 60 * 60 * 1000) * 7; break;
         case 5: newStart -= (24 * 60 * 60 * 1000) * 30; break;
         case 6: newStart -= (24 * 60 * 60 * 1000) * 90; break;
         case 7: newStart -= (24 * 60 * 60 * 1000) * 120; break;
         case 8: newStart -= (24 * 60 * 60 * 1000) * 180; break;
         case 9: newStart -= (24 * 60 * 60 * 1000) * 365; break;
         case 10: newStart -= (24 * 60 * 60 * 1000) * 365 * 2; break;
         case 11: newStart -= (24 * 60 * 60 * 1000) * 365 * 5; break;
         case 12: newStart -= (24 * 60 * 60 * 1000) * 365 * 10; break;
         case 13: newStart -= (24 * 60 * 60 * 1000) * 365 * 20; break;
         case 14: newStart -= (24 * 60 * 60 * 1000) * 365 * 30; break;
      }

      this.props.actions.setDateRange({
         range: parseInt(e.target.value),
         dateStartEnabled,
         dateEndEnabled,
         dateStart: DateTime.toYYYYMMDD(newStart),
         dateEnd: DateTime.toYYYYMMDD(newEnd)
      });

   }

   private onPageChange = (page: number) => {
      this.props.actions.loadCves({ from: (page - 1) * 100 });
   }

   private onCVESelectionChange = (selectedCveIdList: string[]) => {

      if (selectedCveIdList.length > 0) {
         if (this.props.cves.selectedCve?.id === selectedCveIdList[0]) return;
         this.props.actions.selectCve({ cveId: selectedCveIdList[0] });
      } else {
         this.props.actions.selectCve({ cveId: undefined });
      }

   }


}


const mapStateToProps = (state: {
   cves: RCVEListState,
}) => {
   return state;
}


const mapDispatchToProps = (dispatch: Dispatch) => {

   return {
      actions: bindActionCreators(
         Object.assign(
            {},
            slice.actions
         ),
         dispatch
      )
   }

}

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(CVEList);
